import { useEffect, useState } from "react";
import { api, euro, formatDate } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  CheckCircle, CalendarPlus, Kanban, Receipt, PaperPlaneTilt,
  BellRinging, Sparkle, Broom, Clock
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const PRIO_COLORS = { hoch: "border-l-red-500", normal: "border-l-slate-300", niedrig: "border-l-blue-400" };

export default function ActionCenter() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [d, setD] = useState(null);
  const nav = useNavigate();

  const load = () => api.get("/action-center").then(r => setD(r.data));
  useEffect(() => { load(); }, []);

  if (!d) return null;

  const totalOpen =
    (d.pending_bookings?.length || 0) +
    (d.open_tasks?.length || 0) +
    (d.pending_messages || 0) +
    (d.pending_invoices?.length || 0) +
    (d.admin_alerts?.length || 0);

  const completeTask = async (id) => {
    try { await api.post(`/action-center/tasks/${id}/complete`); toast.success("Erledigt"); load(); }
    catch { toast.error("Fehler"); }
  };
  const confirmBooking = async (id) => {
    try { await api.post(`/action-center/bookings/${id}/confirm`); toast.success("Buchung bestätigt"); load(); }
    catch { toast.error("Fehler"); }
  };
  const dismissAlert = async (id) => {
    try { await api.post(`/action-center/alerts/${id}/read`); load(); }
    catch { toast.error("Fehler"); }
  };

  // Batch-Handler: erledigt alle Einträge einer Sektion auf einen Rutsch
  const runBulk = async (items, fn, successMsg) => {
    if (!items.length) return;
    const results = await Promise.allSettled(items.map(fn));
    const ok = results.filter(r => r.status === "fulfilled").length;
    const fail = results.length - ok;
    if (ok) toast.success(`${successMsg} (${ok})`);
    if (fail) toast.error(`${fail} fehlgeschlagen`);
    load();
  };
  const completeAllTasks = () => runBulk(
    (d.open_tasks || []).filter(t => t.kind !== "checklist" && t.id),
    (t) => api.post(`/action-center/tasks/${t.id}/complete`),
    "Alle Aufgaben erledigt",
  );
  const confirmAllBookings = () => runBulk(
    d.pending_bookings || [],
    (b) => api.post(`/action-center/bookings/${b.id}/confirm`),
    "Alle Buchungen bestätigt",
  );
  const dismissAllAlerts = () => runBulk(
    d.admin_alerts || [],
    (a) => api.post(`/action-center/alerts/${a.id}/read`),
    "Alle Alerts gelesen",
  );

  const Section = ({ icon: Icon, title, count, children, empty, testid, onCompleteAll, completeAllLabel = "Alle erledigen", completeAllTestid }) => (
    <div className="msb-card" data-testid={testid}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon size={20} weight="duotone" className="text-primary"/>
          <h3 className="font-semibold truncate" style={{fontFamily:"'Work Sans',sans-serif"}}>{title}</h3>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {count > 0 && onCompleteAll && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2.5 rounded-lg text-xs"
              onClick={onCompleteAll}
              data-testid={completeAllTestid}
              title={completeAllLabel}
            >
              <CheckCircle size={14} weight="duotone" className="mr-1"/>
              {completeAllLabel}
            </Button>
          )}
          {count > 0 && (
            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
              {count}
            </span>
          )}
        </div>
      </div>
      {count === 0 ? (
        <p className="text-xs text-muted-foreground">{empty}</p>
      ) : (
        <div className="space-y-2">{children}</div>
      )}
    </div>
  );

  return (
    <div className="msb-card border-l-4 border-l-primary bg-gradient-to-br from-white to-emerald-50/40" data-testid="action-center">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-primary flex items-center justify-center">
            <Sparkle size={22} weight="duotone" color="white"/>
          </div>
          <div>
            <h2 className="text-xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Action Center</h2>
            <p className="text-xs text-muted-foreground">
              {totalOpen === 0 ? "Alles erledigt · nichts wartet auf Sie" : `${totalOpen} offene Punkte`}
            </p>
          </div>
        </div>
        {isAdmin && (d.pending_messages > 0) && (
          <Button variant="outline" onClick={()=>nav("/freigaben")} className="rounded-xl" data-testid="open-approvals-btn">
            <PaperPlaneTilt size={16}/> <span className="ml-1.5">{d.pending_messages} Nachricht(en) freigeben</span>
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isAdmin && (
          <Section icon={BellRinging} title="Neue Aktionen" count={d.admin_alerts.length}
                   empty="Keine neuen Alerts" testid="section-alerts"
                   onCompleteAll={dismissAllAlerts}
                   completeAllTestid="dismiss-all-alerts-btn">
            {d.admin_alerts.slice(0, 5).map(a => (
              <div key={a.id} className="flex items-start gap-2 p-2 rounded-lg bg-white border border-border" data-testid={`alert-${a.id}`}>
                <div className="mt-0.5" data-testid={`dismiss-alert-${a.id}`}>
                  <Checkbox checked={false} onCheckedChange={()=>dismissAlert(a.id)}/>
                </div>
                <div className="flex-1 text-sm">
                  <div className="font-medium">{a.title}</div>
                  <div className="text-xs text-muted-foreground">{a.detail}</div>
                </div>
              </div>
            ))}
          </Section>
        )}

        {isAdmin && (
          <Section icon={CalendarPlus} title="Buchungen bestätigen" count={d.pending_bookings.length}
                   empty="Keine offenen Anfragen" testid="section-bookings"
                   onCompleteAll={confirmAllBookings}
                   completeAllLabel="Alle bestätigen"
                   completeAllTestid="confirm-all-bookings-btn">
            {d.pending_bookings.slice(0, 5).map(b => (
              <div key={b.id} className="flex items-start gap-2 p-2 rounded-lg bg-white border border-border" data-testid={`booking-${b.id}`}>
                <div data-testid={`confirm-booking-${b.id}`}>
                  <Checkbox checked={false} onCheckedChange={()=>confirmBooking(b.id)}/>
                </div>
                <div className="flex-1 text-sm">
                  <div className="font-medium">{b.guest?.first_name} {b.guest?.last_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {b.room?.name} · {formatDate(b.check_in)} → {formatDate(b.check_out)} · {b.source}
                  </div>
                </div>
              </div>
            ))}
          </Section>
        )}

        <Section icon={Kanban} title="Offene Aufgaben" count={d.open_tasks.length}
                 empty="Keine offenen Aufgaben" testid="section-tasks"
                 onCompleteAll={
                   (d.open_tasks || []).some(t => t.kind !== "checklist" && t.id)
                     ? completeAllTasks : null
                 }
                 completeAllTestid="complete-all-tasks-btn">
          {d.open_tasks.slice(0, 10).map(t => {
            // Reinigungs-Checkliste (virtuelle Aufgabe): Klick → /aufgaben
            if (t.kind === "checklist") {
              const dl = t.due_date;
              const overdue = dl && new Date(dl).getTime() < Date.now();
              const dlText = dl ? new Date(dl).toLocaleString("de-DE",{day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"}) : null;
              return (
                <button key={t.id} type="button" onClick={()=>nav("/aufgaben")}
                        className={`w-full text-left flex items-start gap-2 p-2 rounded-lg bg-white border border-border border-l-4 hover:bg-emerald-50/40 transition ${overdue ? "border-l-red-500" : "border-l-amber-400"}`}
                        data-testid={`checklist-task-${t.room_id}`}>
                  <Broom size={18} weight="duotone" className={`mt-0.5 flex-shrink-0 ${overdue ? "text-red-600" : "text-amber-600"}`}/>
                  <div className="flex-1 text-sm">
                    <div className="font-medium">{t.title}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
                      <span className={`font-semibold ${overdue ? "text-red-700" : "text-amber-800"}`}>
                        {t.open}/{t.total} offen
                      </span>
                      {dlText && (
                        <>
                          <span>·</span>
                          <span className={`inline-flex items-center gap-0.5 font-semibold ${overdue ? "text-red-700" : "text-foreground"}`}>
                            <Clock size={11} weight={overdue ? "fill" : "duotone"}/>
                            bis {dlText}{overdue ? " · überfällig" : ""}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              );
            }
            // Normale Aufgabe
            return (
              <div key={t.id} className={`flex items-start gap-2 p-2 rounded-lg bg-white border border-border border-l-4 ${PRIO_COLORS[t.priority] || ""}`} data-testid={`task-${t.id}`}>
                <div data-testid={`complete-task-${t.id}`}>
                  <Checkbox checked={false} onCheckedChange={()=>completeTask(t.id)}/>
                </div>
                <div className="flex-1 text-sm">
                  <div className="font-medium">{t.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {t.room_name && `${t.room_name} · `}
                    {t.due_date && `Fällig: ${formatDate(t.due_date)}`}
                    {t.priority !== "normal" && ` · ${t.priority}`}
                  </div>
                </div>
              </div>
            );
          })}
        </Section>

        {isAdmin && (
          <Section icon={Receipt} title="Rechnungen zur Prüfung" count={d.pending_invoices.length}
                   empty="Keine offenen Rechnungen" testid="section-invoices">
            {d.pending_invoices.slice(0, 5).map(i => (
              <div key={i.id} className="flex items-start gap-2 p-2 rounded-lg bg-white border border-border" data-testid={`invoice-${i.id}`}>
                <div className="flex-1 text-sm">
                  <div className="font-mono text-xs font-semibold">{i.number}</div>
                  <div className="text-xs text-muted-foreground">
                    {i.guest_name} · {euro(i.gross)} · {i.status}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="rounded-lg" onClick={()=>nav("/rechnungen")}>Ansehen</Button>
              </div>
            ))}
          </Section>
        )}
      </div>
    </div>
  );
}
